"use client";

import React from 'react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import type { ClientPreferences, PreferenceProvenanceEntry } from '@/types/preferences';

type PreferenceField = 'spokesperson' | 'tone' | 'mediaContact';

interface PreferenceChipsProps {
  preferences: ClientPreferences | null;
  provenance?: Record<string, PreferenceProvenanceEntry | undefined>;
  onOpen: (field?: PreferenceField) => void;
  className?: string;
}

function formatSourceLabel(source?: string): string {
  if (!source) return 'unspecified';
  if (source === 'session_override') return 'session';
  if (source === 'user_client_explicit') return 'explicit';
  if (source === 'user_client_inferred') return 'inferred';
  if (source === 'client_baseline') return 'client';
  if (source === 'stylebook_default') return 'stylebook';
  return source.replace(/_/g, ' ');
}

export function PreferenceChips({ preferences, provenance, onOpen, className }: PreferenceChipsProps) {
  if (!preferences) return null;

  const chips: Array<{ field: PreferenceField; label: string; value: string; provenance?: PreferenceProvenanceEntry }> = [];
  if (preferences.spokesperson?.name) {
    chips.push({
      field: 'spokesperson',
      label: 'Spokesperson',
      value: preferences.spokesperson.name,
      provenance: provenance?.spokesperson,
    });
  }
  if (preferences.tone) {
    chips.push({
      field: 'tone',
      label: 'Tone',
      value: preferences.tone.toString(),
      provenance: provenance?.tone,
    });
  }
  if (preferences.mediaContact?.email || preferences.mediaContact?.name) {
    chips.push({
      field: 'mediaContact',
      label: 'Media contact',
      value: preferences.mediaContact.email || preferences.mediaContact.name,
      provenance: provenance?.mediaContact,
    });
  }

  if (!chips.length) return null;

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {chips.map((chip) => (
        <Button
          key={chip.field}
          variant="outline"
          size="sm"
          className="h-7 rounded-full px-3 text-xs"
          onClick={() => onOpen(chip.field)}
        >
          <span className="text-muted-foreground">{chip.label}:</span>
          <span className="font-medium text-foreground">{chip.value}</span>
          {chip.provenance && (
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground/80">
              {formatSourceLabel(chip.provenance.source)}
            </span>
          )}
        </Button>
      ))}
    </div>
  );
}
